import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./dashboard.css";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";

interface Post {
  id: number;
  caption: string;
  media_url: string;
  media_type: string;
  created_at: string;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [latestPosts, setLatestPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchLatestActivity();
  }, []);

  const fetchLatestActivity = async () => {
    if (!token) return;

    try {
      setLoading(true);
      const res = await axios.get(`${API}/posts/me?limit=3`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLatestPosts(res.data || []);
    } catch (e: any) {
      console.error("Dashboard activity error:", e);
      setError("Failed to load activity");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="dashboard-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      {/* HERO */}
      <section className="dash-hero">
        <motion.h1
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Your creative workspace
        </motion.h1>

        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.35 }}
        >
          Create, enhance and publish content with AI-powered tools.
        </motion.p>
      </section>

      {/* ACTION GRID */}
      <section className="dash-actions">
        <ActionTile
          title="Create Draft"
          desc="Start writing your next post"
          color="yellow"
          onClick={() => navigate("/create")}
        />
        <ActionTile
          title="AI Generate"
          desc="Let AI write captions, titles & tags"
          color="purple"
          onClick={() => navigate("/ai-generate")}
        />
        <ActionTile
          title="Auto Fix"
          desc="Improve clarity & safety"
          color="blue"
          onClick={() => navigate("/auto-fix")}
        />
        <ActionTile
          title="My Posts"
          desc="View, edit & manage your posts"
          color="green"
          onClick={() => navigate("/my-posts")}
        />
      </section>

      {/* FEED PREVIEW */}
      <section className="dash-feed-preview">
        <h2>Latest activity</h2>

        {loading && (
          <div className="feed-row">
            <div className="feed-box shimmer" />
            <div className="feed-box shimmer" />
            <div className="feed-box shimmer" />
          </div>
        )}

        {error && <div className="dash-error">{error}</div>}

        {!loading && latestPosts.length === 0 && (
          <div className="dash-empty">No activity yet. Create your first post.</div>
        )}

        <div className="feed-row">
          {latestPosts.map((post) => (
            <motion.div
              key={post.id}
              className="feed-box real"
              whileHover={{ scale: 1.03 }}
              onClick={() => navigate("/my-posts")}
            >
              {post.media_type === "image" ? (
                <img src={post.media_url} alt="post" />
              ) : (
                <video src={post.media_url} muted />
              )}
              <div className="feed-overlay">
                <p>{post.caption?.slice(0, 80)}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </motion.div>
  );
}

function ActionTile({
  title,
  desc,
  color,
  onClick,
}: {
  title: string;
  desc: string;
  color: "yellow" | "purple" | "blue" | "green";
  onClick: () => void;
}) {
  return (
    <motion.div
      className={`action-tile ${color}`}
      whileHover={{ y: -6, scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 200 }}
      onClick={onClick}
      style={{ cursor: "pointer" }}
    >
      <h3>{title}</h3>
      <p>{desc}</p>
    </motion.div>
  );
}
